import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn, UserPlus, ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePortalAuth } from "../context/PortalAuth";

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25 transition-colors duration-300";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export default function PortalLogin() {
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot" | "team"
  const [form, setForm] = useState({ email: "", password: "", name: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);
  const nav = useNavigate();
  const { setUser } = usePortalAuth();

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/portal/auth/login", { email: form.email, password: form.password });
        if (data.token) localStorage.setItem("elevare_portal_token", data.token);
        setUser(data);
        toast.success(`Welcome back, ${data.name || "friend"}.`);
        nav("/portal", { replace: true });
      } else if (mode === "register") {
        const { data } = await api.post("/portal/auth/register", form);
        if (data.token) localStorage.setItem("elevare_portal_token", data.token);
        setUser(data);
        toast.success("Portal account created.");
        nav("/portal", { replace: true });
      } else if (mode === "team") {
        const { data } = await api.post("/portal/auth/register-with-code", form);
        if (data.token) localStorage.setItem("elevare_portal_token", data.token);
        setUser(data);
        toast.success("You're in — welcome to the team.");
        nav("/portal", { replace: true });
      } else {
        await api.post("/portal/auth/forgot-password", { email: form.email });
        setSent(true);
      }
    } catch (e) {
      setErr(formatDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a] flex flex-col">
      <header className="max-w-3xl mx-auto w-full px-6 py-8">
        <Link to="/" data-testid="portal-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
        </Link>
      </header>
      <main className="max-w-md w-full mx-auto px-6 pb-16 flex-1 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-8 space-y-6"
        >
          <div>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Client Portal</span>
            <h1 className="mt-2 font-head text-3xl">
              {mode === "login" && "Sign in"}
              {mode === "register" && "Create your account"}
              {mode === "team" && "Join a team"}
              {mode === "forgot" && "Reset password"}
            </h1>
            <p className="mt-1 text-sm text-[#1e3d3a]/60">
              {mode === "login" && "Access your recovery progress, BBB rating trend, and savings tracker."}
              {mode === "register" && "Use the same email you booked your diagnostic with."}
              {mode === "team" && "You were invited to a team account. Create your own login and enter the access code you were given — no identity check needed."}
              {mode === "forgot" && "We'll email you a link to set a new password."}
            </p>
          </div>

          {mode !== "forgot" && (
            <button
              type="button"
              data-testid="portal-google"
              onClick={googleLogin}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white py-3 text-sm text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.4 35.3 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.8 5.6l6.2 5.2C41 34.6 44 29.7 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </button>
          )}

          {mode !== "forgot" && (
            <div className="relative flex items-center gap-3">
              <div className="flex-1 h-px bg-[#1e3d3a]/12" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/45">or</span>
              <div className="flex-1 h-px bg-[#1e3d3a]/12" />
            </div>
          )}

          {sent ? (
            <div data-testid="portal-reset-sent" className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-4 py-4 text-sm text-[#1e3d3a]">
              <Mail className="h-5 w-5 text-[#a3823f] mb-2" />
              If that email is on file, we just sent a reset link. Check your inbox.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              {(mode === "register" || mode === "team") && (
                <input
                  data-testid="portal-name"
                  placeholder="Your name"
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              )}
              <input
                data-testid="portal-email"
                type="email"
                placeholder="Email"
                required
                className={inputCls}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {mode !== "forgot" && (
                <input
                  data-testid="portal-password"
                  type="password"
                  placeholder="Password (8+ characters)"
                  required
                  minLength={8}
                  className={inputCls}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              )}
              {mode === "team" && (
                <input
                  data-testid="portal-team-code"
                  placeholder="Access code (e.g. ABC-123-XYZ)"
                  required
                  className={inputCls}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              )}
              {err && <p data-testid="portal-error" className="text-xs text-[#b23a48]">{err}</p>}
              <button
                data-testid="portal-submit"
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</> :
                  mode === "login" ? <><LogIn className="h-4 w-4" /> Sign in</> :
                  mode === "register" ? <><UserPlus className="h-4 w-4" /> Create account</> :
                  mode === "team" ? <><UserPlus className="h-4 w-4" /> Join team</> :
                  <>Send reset link <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}

          <div className="text-xs text-[#1e3d3a]/60 flex flex-wrap justify-between gap-3">
            {mode === "login" && (
              <>
                <button type="button" data-testid="portal-goto-register" onClick={() => { setMode("register"); setErr(null); setSent(false); }} className="hover:text-[#a3823f]">Need an account?</button>
                <button type="button" data-testid="portal-goto-team" onClick={() => { setMode("team"); setErr(null); setSent(false); }} className="hover:text-[#a3823f]">Have a team code?</button>
                <button type="button" data-testid="portal-goto-forgot" onClick={() => { setMode("forgot"); setErr(null); setSent(false); }} className="hover:text-[#a3823f]">Forgot password?</button>
              </>
            )}
            {(mode === "register" || mode === "team") && (
              <button type="button" data-testid="portal-goto-login" onClick={() => { setMode("login"); setErr(null); }} className="hover:text-[#a3823f]">Already have an account? Sign in</button>
            )}
            {mode === "forgot" && (
              <button type="button" data-testid="portal-goto-login-2" onClick={() => { setMode("login"); setErr(null); setSent(false); }} className="hover:text-[#a3823f]">Back to sign in</button>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
